import { Injectable, OnDestroy } from '@angular/core';
import { CmpsDialog, CmpsDialogRef } from '@cmps/ng-uikit';
import { tap } from 'rxjs/operators';

import {
    takeUntilDestroy,
    TakeUntilDestroy,
} from '../../../common/take-until-destroy.decorator';
import { BaseCallState, Platform } from '../../../platform-call';
import { disuseStore, useDispatcher, useStore } from '../../../store.decorator';
import {
    CallState,
    CallStatusService,
    MeetingRequest,
} from '../../../teams-call';
import { CaResultResponse, HubMethods, SocketService } from '../../core';
import {
    CaCallOption,
    CaMeetingParamsContext,
    CaMeetingRequestContext,
} from '../core';
import { CaAnalysisDialogComponent } from '../dialog/ca-analysis-dialog.component';
import { caAnalysisDialogReducer } from '../dialog/ca-analysis-dialog.reducer';
import { CaAnalysisDialogState } from '../dialog/ca-analysis-dialog.state';
import { handleTeamsCallSocketMessage } from '../dialog/function/handle-teams-call-socket-message.function';
import { HandleSocketMessageFns } from '../dialog/model/handle-socket-message-fns.model';
import { changeTranscriptStatus } from '../dialog/state/change-transcript-status.action';
import { receiveMeetingRequestContext } from '../dialog/state/receive-meeting-request-context.action';
import { receiveResult } from '../dialog/state/receive-result.action';
import { rememberStatusChange } from '../dialog/state/remember-status-change.action';
import { reset } from '../dialog/state/reset.action';

import { CaMeetBackRef } from './ca-meet-back-ref';

@Injectable({ providedIn: 'root' })
@TakeUntilDestroy()
export class CaMeetService
    implements OnDestroy, CaMeetBackRef, HandleSocketMessageFns
{
    state: CaAnalysisDialogState;

    private _refAnalysis: CmpsDialogRef<CaAnalysisDialogComponent, void>;

    private _commitmentId: number;
    private _callOpts: CaCallOption[];
    private _guideCtx: CaMeetingParamsContext;

    constructor(
        private readonly _dialog: CmpsDialog,
        private readonly _socketService: SocketService,
        readonly callStatusService: CallStatusService,
    ) {
        localStorage.setItem('preNotes', '');

        this._bind();

        useStore(this, 'coaching.caAnalysis', caAnalysisDialogReducer);
    }

    ngOnDestroy(): void {
        this.stop();

        disuseStore(this);
    }

    start(callOpts: CaCallOption[]): void {
        this.stop();

        this._callOpts = callOpts;
        this.startPicker();
    }

    stop(): void {
        this._refAnalysis?.close();

        this._callOpts = null;
        this._guideCtx = null;
    }

    setCommitmentId(commitmentId: number | null): void {
        this._commitmentId = commitmentId;
        if (!this._socketService.connected) {
            return;
        }
        void this._socketService.retrieveCaMeetingRequestContext({
            commitmentId,
        });
    }

    startPicker(): void {
        this._guideCtx = { guideConfigId: 'default' };
        this.startCall(this._callOpts[0].platform);
    }

    startAnalysis(): void {
        if (this._refAnalysis != null && !this._refAnalysis.closed) {
            return;
        }

        this._refAnalysis = this._dialog.open(CaAnalysisDialogComponent, {
            disableOverlayOverflowHidden: true,
            overlayClass: 'cmps-ca-analysis-dialog--overlay-class',
            portalWrapperClass: 'cmps-ca-analysis-dialog--portal-wrapper-class',
            disableBackdrop: true,
            disableClose: true,
            placement: 'top-left',
        });
    }

    startCall(platform: Platform): void {
        const callOpt = this._callOpts?.find((e) => e.platform === platform);
        if (callOpt == null) {
            return;
        }

        switch (callOpt.platform) {
            case Platform.MICROSOFT: {
                const opts: MeetingRequest = callOpt.platformOpts;

                opts.streamTranscript =
                    this.state?.meetingRequestContext?.evaluationEnabled;

                const ctx: CaMeetingParamsContext = JSON.parse(opts.context);
                ctx.guideConfigId = this._guideCtx.guideConfigId;
                opts.context = JSON.stringify(ctx);

                this.callStatusService.showCallDialog(opts);
                break;
            }
        }
    }

    /** @private */
    _callHandleId(): string | null {
        return this.callStatusService.callHandle?.callHandleId;
    }

    /** @private */
    _meetingStarted(): void {
        // see meetingRequestContext?.evaluationEnabled
        if (this.callStatusService.callHandle?.streamTranscript === true) {
            this.startAnalysis();
            void this._socketService.retrieveCaResult();
        }
    }

    /** @private */
    _initialTranscriptStreamed(): void {
        useDispatcher(this, changeTranscriptStatus({ status: 'green' }));
    }

    /** @private */
    _meetingEnded(): void {
        this._refAnalysis?.close();
    }

    /** @private */
    _rememberStatusChange(status: string): void {
        useDispatcher(this, rememberStatusChange({ status }));
    }

    /** @private */
    _reset(): void {
        useDispatcher(this, reset({}));
    }

    private _bind(): void {
        this.callStatusService.socketService.message
            .pipe(
                tap((msg) => {
                    handleTeamsCallSocketMessage(msg, this);
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        if (this._socketService.connected) {
            /* TODO move evaluationBackgroundImage out of meetingRequestContext */
            void this._socketService.retrieveCaMeetingRequestContext({
                commitmentId: this._commitmentId,
            });
        }
        this._socketService.didConnect
            .pipe(
                tap(() => {
                    /* TODO move evaluationBackgroundImage out of meetingRequestContext */
                    void this._socketService.retrieveCaMeetingRequestContext({
                        commitmentId: this._commitmentId,
                    });
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        this._socketService.message
            .pipe(
                tap((msg) => {
                    switch (msg.hubMethod) {
                        case HubMethods.OnCaMeetingRequestContext:
                            this._onCaMeetingRequestContext(msg.payload);
                            break;
                        case HubMethods.OnCaResult:
                            this._onCaResult(msg.payload);
                            break;
                    }
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    private _onCaMeetingRequestContext(
        payload: CaMeetingRequestContext | null,
    ): void {
        useDispatcher(
            this,
            receiveMeetingRequestContext({
                caMeetingRequestContext: payload,
            }),
        );
    }

    private _onCaResult(payload: CaResultResponse | null): void {
        const noTeamsCall =
            this.callStatusService.callHandle?.callHandleId == null ||
            payload?.callHandleId !==
                this.callStatusService.callHandle?.callHandleId ||
            this.state.statuses.includes(CallState.MeetingEnded) ||
            this.state.statuses.includes(BaseCallState.Ended) ||
            payload?.caResult == null;

        if (noTeamsCall) {
            return;
        }

        if (this.callStatusService.callHandle?.streamTranscript === true) {
            this.startAnalysis();
        }

        useDispatcher(this, receiveResult({ result: payload.caResult }));
    }
}
